import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import {
  apiErrorMessage, buildExerciseRequest, buildExerciseUpdateRequest, formFromEntry,
} from './prescriptionForm';
import { filterExercises } from './exerciseBrowser';
import {
  formForNextLine, defaultSessionName, predictedAddition, withPrediction, intensityOf,
  dayChoices, moveRequest,
} from './weekEdit';
import { usePreferences } from './usePreferences';
import {
  orderedDays, linesOf, densityOf, balanceRows, labelFor, unclassifiedCaveat,
  hasProgramming, ZOOM, ZOOM_LEVELS, zoomHref, focusedDay,
} from './weekBuild';
import '../styles/WeekBuildPage.css';

// The week a block is written in.
//
// # WHY THIS EXISTS ALONGSIDE THE PHASE GRID
//
// The two read different axes and neither substitutes for the other. The phase
// grid reads ACROSS WEEKS for one exercise — is this getting heavier. This reads
// ACROSS DAYS within one week — is Thursday going to wreck what Tuesday did.
//
// # WHY EVERY DAY STAYS LEGIBLE
//
// The whole value of the layout is peripheral vision: a coach adding to
// Wednesday can see what Monday and Friday already ask for, so they never hold
// the rest of the week in their head. That is why nothing here opens in a modal
// and why the columns do not collapse — a dialog over the top would cost exactly
// the thing the view is for.

const PATTERN_CLASS = {
  'horizontal-push': 'p-hpush',
  'horizontal-pull': 'p-hpull',
  'vertical-push': 'p-vpush',
  'vertical-pull': 'p-vpull',
  squat: 'p-squat',
  hinge: 'p-hinge',
  lunge: 'p-lunge',
  unclassified: 'p-unclassified',
};

/** The strip: what this week is made of, and where it is thin. */
const BalanceStrip = ({ week }) => {
  const rows = balanceRows(week);
  const caveat = unclassifiedCaveat(week);

  if (!hasProgramming(week)) {
    return (
      <section className="card week-balance week-balance-empty">
        <h2 className="section-title">Balance</h2>
        <p className="meta-copy">
          Nothing programmed yet. Balance reads across the whole week, so it has
          something to say once there is work on more than one day.
        </p>
      </section>
    );
  }

  return (
    <section className="card week-balance" aria-labelledby="week-balance-title">
      <div className="week-balance-head">
        <h2 className="section-title" id="week-balance-title">Balance</h2>
        <span className="meta-copy">
          {week.sets} set{week.sets === 1 ? '' : 's'} across the week
        </span>
      </div>

      {caveat && <p className="week-caveat" role="note">{caveat}</p>}

      {rows.length === 0 && (
        <p className="meta-copy">
          No opposing patterns programmed yet — a pair needs work on both sides
          before there is anything to compare.
        </p>
      )}

      <div className="week-pairs">
        {rows.map((row) => (
          <div className="week-pair" key={row.name}>
            <span className="week-pair-name">{row.name}</span>
            <div className="week-pair-bars">
              {[row.a, row.b].map((side) => (
                <div className="week-side" key={side.pattern}>
                  <span className="week-side-label">{labelFor(week, side.pattern)}</span>
                  <span className={`week-bar${side.thin ? ' is-thin' : ''}`}>
                    <span
                      className={`week-bar-fill ${PATTERN_CLASS[side.pattern] || ''}`}
                      style={{ width: `${Math.max(side.share * 100, 2)}%` }}
                    />
                  </span>
                  <span className="week-side-sets mono">{side.sets}</span>
                </div>
              ))}
            </div>
            {/* The judgement is the server's, made over the whole week. A day is
                never flagged: Monday pushes and Thursday pulls, and neither is
                balanced on its own. */}
            {row.thin && (
              <p className="week-thin" role="status">
                Thin on {labelFor(week, row.thin).toLowerCase()} this week.
              </p>
            )}
          </div>
        ))}
      </div>

      <ul className="week-legend">
        {(week.patterns ?? []).map((pattern) => (
          <li key={pattern.pattern}>
            <span className={`week-dot ${PATTERN_CLASS[pattern.pattern] || ''}`} aria-hidden="true" />
            {pattern.label}
            <span className="mono week-legend-sets">{pattern.sets}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

/**
 * Adding a line to a day, without leaving the week.
 *
 * # WHY THIS IS NOT A MODAL
 *
 * The whole value of the layout is that the other days stay legible. A dialog
 * over the top would cost exactly that, and it is the one thing the competitor's
 * build view refuses too — their exercise editor expands in place with the
 * neighbouring day still visible at the screen edge.
 */
const AddLine = ({ exercises, form, setForm, onPick, picked, onSave, onCancel, busy, error }) => {
  const [search, setSearch] = useState('');
  const matches = search.trim().length < 2
    ? []
    : filterExercises(exercises ?? [], { search }).slice(0, 6);

  return (
    <div className="week-add">
      {!picked && (
        <label className="week-add-search">
          <span className="sr-only">Find an exercise</span>
          <input
            type="text"
            className="field"
            placeholder="Find an exercise"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </label>
      )}

      {!picked && matches.length > 0 && (
        <ul className="week-add-matches">
          {matches.map((exercise) => (
            <li key={exercise.id}>
              <button type="button" className="week-add-match" onClick={() => onPick(exercise)}>
                <span>{exercise.name}</span>
                <span className="week-add-match-cat">{exercise.category}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!picked && search.trim().length >= 2 && matches.length === 0 && (
        <p className="meta-copy week-add-none">Nothing in the library matches that.</p>
      )}

      {picked && (
        <>
          <p className="week-add-picked">
            <strong>{picked.name}</strong>
            <button type="button" className="text-link" onClick={() => onPick(null)}>
              Change
            </button>
          </p>

          <div className="week-add-fields">
            {[
              ['series', 'Series'], ['sets', 'Sets'], ['reps', 'Reps'],
              ['tempo', 'Tempo'], ['rest', 'Rest (s)'],
            ].map(([field, label]) => (
              <label className="week-add-field" key={field}>
                <span>{label}</span>
                <input
                  type="text"
                  className="field"
                  value={form[field] ?? ''}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                />
              </label>
            ))}
          </div>

          {error && <p className="status-error" role="alert">{error}</p>}

          <div className="week-add-actions">
            <button type="button" className="btn-primary" onClick={onSave} disabled={busy}>
              {busy ? 'Adding…' : 'Add'}
            </button>
            <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
          </div>
        </>
      )}

      {!picked && (
        <div className="week-add-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      )}
    </div>
  );
};

/**
 * Editing a line where it sits.
 *
 * # WHY THE ROW BECOMES THE FORM
 *
 * The alternative is a dialog, and a dialog covers the neighbouring days — which
 * is the one thing this view exists to keep. The row grows, the rows around it
 * stay put, and the coach can still read Thursday while changing Tuesday.
 *
 * # WHY INTENSITY IS HERE AND NOT ONLY IN THE SESSION EDITOR
 *
 * Intensity is the field a coach revises most while balancing a week — it is how
 * a day gets lighter without losing its shape. Sending them to another screen for
 * it is what made the week view a reference document.
 */
const EditLine = ({ form, setForm, onSave, onCancel, onDelete, busy, error }) => (
  <div className="week-add week-edit">
    <div className="week-add-fields">
      {/* Ordered so the grid flows without holes, and the two fields that hold
          TEXT rather than a number get double width. A rep scheme is
          "11,9,7,5" and a tempo is "4-0-1-0"; at one column each they clipped
          their own values, so a coach could not read what they were editing. */}
      {[
        ['series', 'Series'], ['sets', 'Sets'], ['rest', 'Rest (s)'],
        ['reps', 'Reps', true], ['percentOneRM', '%1RM'],
        ['tempo', 'Tempo', true], ['load', 'Load'],
        ['rpe', 'RPE'], ['rir', 'RIR'],
      ].map(([field, label, wide]) => (
        <label className={`week-add-field${wide ? ' is-wide' : ''}`} key={field}>
          <span>{label}</span>
          <input
            type="text"
            className="field"
            value={form[field] ?? ''}
            onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          />
        </label>
      ))}
    </div>

    <label className="week-add-field">
      <span>Notes</span>
      <input
        type="text"
        className="field"
        value={form.notes ?? ''}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
      />
    </label>

    {error && <p className="status-error" role="alert">{error}</p>}

    <div className="week-add-actions">
      <button type="button" className="btn-primary" onClick={onSave} disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
      <button type="button" className="btn-ghost week-edit-delete" onClick={onDelete}>
        Remove
      </button>
    </div>
  </div>
);

/**
 * The three magnifications of one block.
 *
 * # WHY THIS IS ONE CONTROL AND NOT THREE LINKS
 *
 * They are the same object at different distances, and a coach moves between
 * them constantly while writing: check the week is balanced, zoom into Thursday
 * to fix it, zoom out to see whether that worked. Three links scattered across
 * two screens makes that a navigation task; one control makes it a zoom.
 *
 * Each rung says what it ANSWERS rather than what it shows. "Week" is a noun and
 * tells a coach nothing about why they would press it.
 */
const ZoomLadder = ({ programId, phaseId, current, dayOfWeek }) => (
  <nav className="zoom-ladder" aria-label="How closely to look at this block">
    {ZOOM_LEVELS.map((level) => {
      const active = level.id === current;
      return (
        <Link
          key={level.id}
          to={zoomHref({ programId, phaseId, level: level.id, dayOfWeek })}
          className={`zoom-rung${active ? ' is-current' : ''}`}
          aria-current={active ? 'page' : undefined}
        >
          <span className="zoom-rung-label">{level.label}</span>
          <span className="zoom-rung-asks">{level.asks}</span>
        </Link>
      );
    })}
  </nav>
);

/**
 * Sending a day somewhere else.
 *
 * # WHY A MENU AND NOT DRAG-AND-DROP
 *
 * Dragging is the obvious answer and the wrong one. HTML5 drag events do not
 * fire on touch at all, and this is a phone-first app — a coach writing a block
 * on the way to the gym would get a feature that silently does nothing. It is
 * also invisible to a keyboard and a screen reader unless a parallel mechanism
 * exists anyway, which is this one.
 *
 * Dragging can be layered on for a mouse later. It cannot be the only way.
 */
const DayMenu = ({ choices, open, onToggle, onMove, onCopy, sessionName }) => (
  <div className="week-session-menu">
    <button
      type="button"
      className="week-session-more"
      aria-expanded={open}
      aria-label={`Move or copy ${sessionName}`}
      onClick={onToggle}
    >
      ⋯
    </button>

    {open && (
      <div className="week-menu-sheet" role="menu">
        <p className="week-menu-heading">Move to</p>
        {choices.map((choice) => (
          <button
            key={`move-${choice.dayOfWeek}`}
            type="button"
            role="menuitem"
            className="week-menu-item"
            onClick={() => onMove(choice.dayOfWeek)}
          >
            {choice.label}
            {/* Said on the item, because "move to Thursday" reads very
                differently when Thursday already has nine sets on it. */}
            {choice.occupied && <span className="week-menu-note">has work</span>}
          </button>
        ))}

        <p className="week-menu-heading">Copy to</p>
        {choices.map((choice) => (
          <button
            key={`copy-${choice.dayOfWeek}`}
            type="button"
            role="menuitem"
            className="week-menu-item"
            onClick={() => onCopy(choice.dayOfWeek)}
          >
            {choice.label}
            {choice.occupied && <span className="week-menu-note">has work</span>}
          </button>
        ))}
      </div>
    )}
  </div>
);

/** One day, with everything written on it. */
const DayColumn = ({
  day, programId, phaseId, zoomed, exercises, onAdd, onDelete, adding, onOpenAdd, onCloseAdd,
  form, setForm, picked, onPick, busy, error,
  editingId, onOpenEdit, onCloseEdit, onSaveEdit, editForm, setEditForm, editError,
  choices, menuFor, onToggleMenu, onMoveDay, onCopyDay,
}) => {
  const density = densityOf(day);
  const lines = linesOf(day);

  return (
    <section className="week-day" aria-label={day.label}>
      {/* The day name IS the way in. Clicking the day you want to look at
          closely is the gesture a coach already has; a separate day picker
          would be a second row of controls in a header that just overflowed at
          375. Zoomed in, the name is where you are, so it stops being a link. */}
      <header className="week-day-head">
        {zoomed ? (
          <span className="week-day-name">
            <span className="week-day-short">{day.short}</span>
            <span className="week-day-full">{day.label}</span>
          </span>
        ) : (
          <Link
            className="week-day-name week-day-zoom"
            to={zoomHref({ programId, phaseId, level: ZOOM.DAY, dayOfWeek: day.dayOfWeek })}
            aria-label={`Look closely at ${day.label}`}
          >
            <span className="week-day-short">{day.short}</span>
            <span className="week-day-full">{day.label}</span>
          </Link>
        )}
      </header>

      {day.sessions.length === 0 && (
        <p className="week-day-rest">Rest</p>
      )}

      {day.sessions.map((session) => (
        <div className="week-session" key={session.id}>
          <div className="week-session-head">
            <h3 className="week-session-name">
              <Link to={`/programs/${programId}/sessions/${session.id}/train`}>
                {session.name || 'Untitled session'}
              </Link>
            </h3>
            <DayMenu
              choices={choices}
              open={menuFor === session.id}
              onToggle={() => onToggleMenu(session.id)}
              onMove={(target) => onMoveDay(session, target)}
              onCopy={(target) => onCopyDay(session, target)}
              sessionName={session.name || day.label}
            />
          </div>

          <ul className="week-lines">
            {session.exercises.map((line) => (
              editingId === line.id ? (
                <li className="week-line week-line-editing" key={line.id}>
                  <span
                    className={`week-line-rule ${PATTERN_CLASS[line.pattern] || ''}`}
                    aria-hidden="true"
                  />
                  <div className="week-line-body">
                    <span className="week-line-name">
                      <span className="week-series mono">{editForm.series}</span>
                      {line.exerciseName}
                    </span>
                    <EditLine
                      form={editForm}
                      setForm={setEditForm}
                      onSave={() => onSaveEdit(session.id, line)}
                      onCancel={onCloseEdit}
                      onDelete={() => onDelete(session.id, line)}
                      busy={busy}
                      error={editError}
                    />
                  </div>
                </li>
              ) : (
                <li className="week-line" key={line.id}>
                  <span
                    className={`week-line-rule ${PATTERN_CLASS[line.pattern] || ''}`}
                    aria-hidden="true"
                  />
                  {/* The row itself is the control. A separate pencil would be
                      another 24px target in a column that is already narrow, and
                      the thing a coach wants to press IS the prescription. */}
                  <button
                    type="button"
                    className="week-line-body week-line-open"
                    onClick={() => onOpenEdit(line)}
                  >
                    <span className="week-line-name">
                      <span className="week-series mono">{line.series}</span>
                      {line.exerciseName}
                    </span>
                    <span className="week-line-presc mono">
                      {line.sets} x {line.repScheme}
                      {intensityOf(line) ? ` @ ${intensityOf(line)}` : ''}
                      {line.notes ? ` — ${line.notes}` : ''}
                    </span>
                    {/* The pattern is named, not only coloured. Colour alone puts
                        the one piece of information this view adds out of reach of
                        anybody who cannot distinguish the hues. */}
                    <span className="week-line-pattern">
                      {line.patternLabel}{line.isAccessory ? ' · accessory' : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="week-line-remove"
                    aria-label={`Remove ${line.exerciseName} from ${session.name || day.label}`}
                    onClick={() => onDelete(session.id, line)}
                  >
                    ✕
                  </button>
                </li>
              )
            ))}
          </ul>
        </div>
      ))}

      {adding ? (
        <AddLine
          exercises={exercises}
          form={form}
          setForm={setForm}
          picked={picked}
          onPick={onPick}
          onSave={() => onAdd(day)}
          onCancel={onCloseAdd}
          busy={busy}
          error={error}
        />
      ) : (
        <button
          type="button"
          className="week-add-open"
          onClick={() => onOpenAdd(day, lines)}
        >
          + Add exercise
        </button>
      )}

      <footer className="week-day-foot">{density || '—'}</footer>
    </section>
  );
};

const WeekBuildPage = () => {
  const { programId, phaseId } = useParams();
  const [params] = useSearchParams();
  const { token } = useAuth();
  const { unit, loadRange } = usePreferences();

  const [week, setWeek] = useState(null);
  const [error, setError] = useState(null);
  const [library, setLibrary] = useState([]);

  // Which day is being added to, and what is being added.
  const [addingTo, setAddingTo] = useState(null);
  const [form, setForm] = useState({});
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addError, setAddError] = useState(null);

  // Which line is open for editing, and its working copy.
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState(null);

  // Which session's move/copy menu is open. One at a time.
  const [menuFor, setMenuFor] = useState(null);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const loadWeek = useCallback(async () => {
    const { data } = await axios.get(
      buildApiUrl(`/programs/${programId}/phases/${phaseId}/week`), authConfig()
    );
    setWeek(data);
    return data;
  }, [programId, phaseId, authConfig]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [, exercises] = await Promise.all([
          loadWeek(),
          axios.get(buildApiUrl('/exercises'), authConfig()),
        ]);
        if (!live) return;
        const list = exercises.data;
        setLibrary(Array.isArray(list) ? list : (list.exercises ?? []));
      } catch (err) {
        if (live) setError(apiErrorMessage(err, 'Could not load this week.'));
      }
    })();
    return () => { live = false; };
  }, [loadWeek, authConfig]);

  const openAdd = (day, lines) => {
    closeEdit();
    setAddingTo(day.dayOfWeek);
    setForm(formForNextLine(lines));
    setPicked(null);
    setAddError(null);
  };

  const closeAdd = () => {
    setAddingTo(null);
    setPicked(null);
    setAddError(null);
  };

  /**
   * Adds the line, creating a session on the day first if there is not one.
   *
   * An empty day has no session to attach a prescription to, and making the
   * coach create one before they can add anything would put a form in front of
   * the act they came here to do.
   */
  const addLine = async (day) => {
    if (!picked) return;

    // buildExerciseRequest answers {body} or {errors}; it runs the same
    // validation as the programme editor, so a prescription that is refused
    // there is refused here for the same reason and in the same words.
    const { body, errors } = buildExerciseRequest(form, picked, unit, loadRange);
    if (errors) {
      setAddError(Object.values(errors)[0]);
      return;
    }

    setBusy(true);
    setAddError(null);
    try {
      let sessionId = day.sessions[day.sessions.length - 1]?.id;
      if (!sessionId) {
        const { data } = await axios.post(
          buildApiUrl(`/programs/${programId}/sessions`),
          {
            name: defaultSessionName(day),
            phaseId: Number(phaseId),
            dayOfWeek: day.dayOfWeek,
          },
          authConfig()
        );
        sessionId = data.id ?? data.session?.id;
        if (!sessionId) throw new Error('the session was created without an id');
      }

      await axios.post(
        buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises`),
        body,
        authConfig()
      );

      await loadWeek();
      // Left open, with the series advanced: a coach adding A1 is usually about
      // to add A2, and closing the form after every line would make the common
      // case the slow one.
      const lines = linesOf(day);
      setForm(formForNextLine([...lines, {
        series: form.series, sets: form.sets, repScheme: form.reps,
        tempo: form.tempo, rest: form.rest,
      }]));
      setPicked(null);
    } catch (err) {
      setAddError(apiErrorMessage(err, 'Could not add that exercise.'));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (line) => {
    // Adding and editing are mutually exclusive: two open forms in one narrow
    // column is two things competing for the same fifteen rems.
    setAddingTo(null);
    setPicked(null);
    setEditingId(line.id);
    setEditForm(formFromEntry(line));
    setEditError(null);
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const saveEdit = async (sessionId, line) => {
    // The same builder the programme editor uses, so a prescription refused
    // there is refused here for the same reason and in the same words.
    const { body, errors } = buildExerciseUpdateRequest(editForm, unit, loadRange);
    if (errors) {
      setEditError(Object.values(errors)[0]);
      return;
    }

    setBusy(true);
    setEditError(null);
    try {
      await axios.put(
        buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${line.id}`),
        body,
        authConfig()
      );
      await loadWeek();
      closeEdit();
    } catch (err) {
      setEditError(apiErrorMessage(err, 'Could not save that change.'));
    } finally {
      setBusy(false);
    }
  };

  const toggleMenu = (sessionId) => setMenuFor((open) => (open === sessionId ? null : sessionId));

  /**
   * Moves a day, or copies it, and reloads.
   *
   * Both go through the week reload rather than patching state locally: moving a
   * session changes two columns and the balance strip does not change at all,
   * and reasoning about which of those to touch by hand is how a view starts
   * disagreeing with its own database.
   */
  const sendDay = async (session, dayOfWeek, mode) => {
    setMenuFor(null);
    setError(null);
    try {
      if (mode === 'move') {
        await axios.put(
          buildApiUrl(`/programs/${programId}/sessions/${session.id}`),
          moveRequest(session, phaseId, dayOfWeek),
          authConfig()
        );
      } else {
        await axios.post(
          buildApiUrl(`/programs/${programId}/sessions/${session.id}/copy`),
          { dayOfWeek },
          authConfig()
        );
      }
      await loadWeek();
    } catch (err) {
      setError(apiErrorMessage(
        err,
        mode === 'move' ? 'Could not move that day.' : 'Could not copy that day.'
      ));
    }
  };

  // A misclick on ✕ used to delete with no way back (UX audit D5), on the
  // screen where a coach makes the most edits. It asks first, with the same
  // wording as the editor's own delete.
  const deleteLine = async (sessionId, line) => {
    if (!window.confirm(`Delete "${line.exerciseName}" from this session?`)) return;
    try {
      await axios.delete(
        buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${line.id}`),
        authConfig()
      );
      closeEdit();
      await loadWeek();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove that exercise.'));
    }
  };

  if (error) {
    return <BaseLayout><p className="status-error" role="alert">{error}</p></BaseLayout>;
  }
  if (!week) {
    return <BaseLayout><p className="meta-copy" role="status">Loading the week…</p></BaseLayout>;
  }

  const days = orderedDays(week);

  // ?day=N is a LENS on the week, not a different resource. Pressing back
  // returns to the week rather than to a list, and a link to one day is a link
  // somebody else can open.
  const requestedDay = params.get('day');
  const zoomed = requestedDay !== null;
  const focused = zoomed ? focusedDay(week, requestedDay) : null;
  const shownDays = focused ? [focused] : days;

  // The strip moves as the coach types. The server stays the authority — the
  // week is refetched on every write — but a coach adding a fourth pressing
  // movement should see the pull side go thin AS THEY PICK IT, not a round trip
  // later. The prediction is discarded the moment the real answer lands.
  const shown = withPrediction(week, predictedAddition(picked, form)) || week;

  return (
    <BaseLayout>
      <div className="week-build">
        <header className="week-header">
          {/* ?day=N shows one day, so it says so (UX audit D7): the week's
              title and subtitle on a single column read as a broken page. */}
          <span className="pill">{focused ? 'Day View' : 'Week View'}</span>
          <h1 className="page-title">{week.phaseName || 'This block'}</h1>
          <p className="meta-copy">
            {focused
              ? `${focused.label}, as it is trained. The balance below still counts the whole week.`
              : 'The week as it is trained. Every day stays readable while you work on one of them.'}
          </p>
        </header>

        <ZoomLadder
          programId={programId}
          phaseId={phaseId}
          current={zoomed ? ZOOM.DAY : ZOOM.WEEK}
          dayOfWeek={focused ? focused.dayOfWeek : (days.find((d) => d.sessions.length)?.dayOfWeek)}
        />

        {/* The WEEK's balance, at every zoom level.
            Balance is a week property — Monday pushes and Thursday pulls, and
            neither is balanced alone. Recomputing this for the focused day would
            flag every upper-body session ever written, and a coach zoomed into
            Monday would read it as Monday's. It says "across the week" for the
            same reason. */}
        <BalanceStrip week={shown} />

        {/* The columns scroll inside their own strip. The page never scrolls
            sideways — the rule the responsive sweep holds everywhere. */}
        <div className="week-columns-scroll" tabIndex={0} role="region" aria-label="The training week, scrolls horizontally">
          <div className={`week-columns${focused ? ' is-zoomed' : ''}`}>
            {shownDays.map((day) => (
              <DayColumn
                key={day.dayOfWeek}
                day={day}
                programId={programId}
                phaseId={phaseId}
                zoomed={Boolean(focused)}
                exercises={library}
                adding={addingTo === day.dayOfWeek}
                onOpenAdd={openAdd}
                onCloseAdd={closeAdd}
                onAdd={addLine}
                onDelete={deleteLine}
                form={form}
                setForm={setForm}
                picked={picked}
                onPick={setPicked}
                busy={busy}
                error={addError}
                editingId={editingId}
                onOpenEdit={openEdit}
                onCloseEdit={closeEdit}
                onSaveEdit={saveEdit}
                editForm={editForm}
                setEditForm={setEditForm}
                editError={editError}
                choices={dayChoices(week, day.dayOfWeek)}
                menuFor={menuFor}
                onToggleMenu={toggleMenu}
                onMoveDay={(session, target) => sendDay(session, target, 'move')}
                onCopyDay={(session, target) => sendDay(session, target, 'copy')}
              />
            ))}
          </div>
        </div>

        {week.unscheduled.length > 0 && (
          <section className="card week-unscheduled">
            <h2 className="section-title">Not on a day yet</h2>
            <p className="meta-copy">
              These are part of the block and count toward the balance above, but
              they have no weekday, so they do not appear in a column.
            </p>
            <ul className="week-lines">
              {week.unscheduled.map((session) => (
                <li key={session.id}>
                  {session.name || 'Untitled session'}
                  <span className="mono week-legend-sets">
                    {session.exercises.length}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </BaseLayout>
  );
};

export default WeekBuildPage;
